import { createSlice, AnyAction } from "@reduxjs/toolkit";
import { updateViewList } from "@reducers/_utils/reducer-helpers";
import type { FileLocationsState } from "@types";

const initialState: FileLocationsState = {
  ctid: "",
  fileLocations: [],
  trashedFiles: [],
  total: 0,
  trashTotal: 0,
  fileManagerBaseUrl: "",
  pageNumber: 1,
  prePageNumber: 1,
  searchValue: "",
  isLoading: false,
};

const filesSlice = createSlice({
  name: "fileLocations",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase("LOAD_FILES_START", (state) => {
        state.isLoading = true;
      })
      .addCase("LOAD_FILES", (state, action: AnyAction) => {
        state.ctid = action.ctid;
        state.fileLocations = action.fileLocations;
        state.total = action.total;
        state.fileManagerBaseUrl = action.fileManagerBaseUrl;
        state.trashedFiles = action.trashedFiles;
        state.trashTotal = action.trashTotal;
        state.searchValue = action.searchValue;
        state.isLoading = false;
      })
      .addCase("LOAD_TRASH", (state, action: AnyAction) => {
        state.trashedFiles = action.trashedFiles;
        state.trashTotal = action.trashTotal;
        state.isLoading = false;
      })
      .addCase("ADD_FILE_LOCATION", (state, action: AnyAction) => {
        state.fileLocations = [action.itemDetails, ...state.fileLocations];
        state.total = state.total + 1;
      })
      .addCase("UPDATE_FILE_ITEM", (state, action: AnyAction) => {
        const keyLookUp =
          action.updateAction === "update_live"
            ? "status"
            : `${action.itemType}_id`;
        const itemDetails =
          action.updateAction === "update" ||
          action.updateAction === "update_live"
            ? action.itemDetails
            : null;
        state.fileLocations = state.fileLocations
          ? state.fileLocations.map((fileLocation: any) =>
              fileLocation.location_name === action.itemDetails.location
                ? {
                    ...fileLocation,
                    files: updateViewList(
                      keyLookUp,
                      action.itemDetails[keyLookUp],
                      [...fileLocation.files],
                      itemDetails
                    ),
                  }
                : fileLocation
            )
          : [];
      })
      .addCase("UPDATE_FILE_LOCATION_ITEM", (state, action: AnyAction) => {
        const keyLookUp = `${action.itemType}_id`;
        const itemDetails =
          action.updateAction === "update" ? action.itemDetails : null;
        state.fileLocations = state.fileLocations
          ? updateViewList(
              keyLookUp,
              action.itemDetails[keyLookUp],
              [...state.fileLocations],
              itemDetails
            )
          : [];
        state.total = state.total - 1 >= 0 ? state.total - 1 : 0;
      })
      .addCase("ADD_FILE_TO_FILE_LOCATION", (state, action: AnyAction) => {
        state.fileLocations = state.fileLocations.map((fileLocation: any) =>
          fileLocation.location_name === action.fileLocationValue
            ? {
                ...fileLocation,
                files: [action.file, ...fileLocation.files],
              }
            : fileLocation
        );
      })
      .addCase("SET_PAGE_NUMBER", (state, action: AnyAction) => {
        state.pageNumber = action.pages.page_number;
        if (action.pages.pre_page_number) {
          state.prePageNumber = action.pages.pre_page_number;
        }
      })
      .addCase("SHOW_FOLDER_FILES", (state, action: AnyAction) => {
        const targetKey =
          action.locationType === "liveScheduled"
            ? "fileLocations"
            : "trashedFiles";
        const targetList = (state as any)[targetKey];
        const updatedList = targetList.map((location: any) => ({
          ...location,
          showFiles:
            location.location_name === action.folderName
              ? action.showFiles
              : location.showFiles,
        }));
        (state as any)[targetKey] = updatedList;
      });
  },
});

export const selectFileLocations = (state: {
  fileLocations: FileLocationsState;
}): FileLocationsState => state.fileLocations;

export default filesSlice.reducer;
